/* eslint-disable camelcase */
import { useQuery } from '@tanstack/react-query';

import Config from 'Config';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { organizationIDEscaped } from 'helpers/account';
import { errorDeserialize } from 'helpers/error-deserializer';
import { productsClient } from 'services/connectivity/products/client';
import { Product, ProductResponse } from 'services/connectivity/products/types';

type RedirectUrlOptions = {
  jwtParams?: Product;
  utmParams?: any;
};

type UseProducts = {
  handleRedirectToProduct: (product: string, organization_id: string) => void;
  getRedirectUrl: (
    product: string,
    organization_id: string,
    options?: RedirectUrlOptions
  ) => string;
  products: ProductResponse;
  isFetchingProducts: boolean;
};

export const fetchProducts = async (): Promise<ProductResponse> => {
  const { error, result } = await productsClient.list();

  if (error) {
    throw errorDeserialize(error);
  }

  return result;
};

export const useProducts = (): UseProducts => {
  const { data: products, isLoading } = useQuery<ProductResponse, string>(
    ['list-products'],
    () => fetchProducts(),
    {
      refetchOnWindowFocus: false,
      initialData: [],
    }
  );

  const getRedirectUrl = (
    product: string,
    organization_id: string,
    options?: RedirectUrlOptions
  ): string => {
    let productParam: Product;
    let utmParams: string;

    if (options?.jwtParams) {
      productParam = options?.jwtParams;
    } else {
      productParam =
        product === 'Accounts'
          ? {
              client_id: import.meta.env.VITE_APP_CLIENT_ID,
              redirect_uri: import.meta.env.VITE_APP_PANEL_URI,
              product: 'Accounts',
              response_type: 'token',
            }
          : products.find((listedProduct) => listedProduct.product === product);
    }

    if (options?.utmParams) {
      utmParams = Object.keys(options.utmParams)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(options.utmParams[key].toString())}`
        )
        .join('&');
    }

    if (productParam?.entrypoint) {
      return `${productParam.entrypoint}?organization_id=${organizationIDEscaped(organization_id)}${utmParams ? `&${utmParams}` : ''}`;
    }

    const hostname = product === 'Accounts' ? Config.accounts_api_url : '/';
    const queryParams = buildOauthQuery(productParam);

    return `${hostname}?organization_id=${organizationIDEscaped(organization_id)}&${queryParams}${utmParams ? `&${utmParams}` : ''}`;
  };

  const handleRedirectToProduct = (product: string, organization_id: string): void => {
    window.location.replace(getRedirectUrl(product, organization_id));
  };

  return {
    handleRedirectToProduct,
    getRedirectUrl,
    products,
    isFetchingProducts: isLoading,
  };
};
